import type { Amount as AmountType } from '@shared/core-types';

import { DECIMAL_PRECISION_MULTIPLIER } from './types';

const HIGHEST_PRECISION = 4;
const BASE_POW_VALUE = 10;
export const DIFFERENT_AMOUNT_CURRENCIES_ERROR = 'Amount currencies have to match.';

interface InputAttributes {
    value: string | number;
    precision: number;
    currency: string;
}

export class Amount {
    attributes: AmountType;

    constructor(attributes: InputAttributes) {
        Amount.validateAttributes(attributes);

        this.attributes = {
            ...attributes,
            value: Number(attributes.value),
        };
    }

    static validateAttributes(attributes: InputAttributes): void {
        if (typeof attributes !== 'object') {
            throw new Error('Missing attributes object!');
        }

        ['value', 'precision', 'currency'].forEach((attribute) => {
            if (!Object.prototype.hasOwnProperty.call(attributes, attribute)) {
                throw new Error(`Missing attribute: ${attribute}`);
            }
        });

        if (attributes.value === undefined || Number.isNaN(attributes.value)) {
            throw new Error('Value is not a number');
        }
    }

    static add(amount1: Amount, amount2: Amount): Amount {
        if (!(amount1 instanceof Amount && amount2 instanceof Amount)) {
            throw new Error('Expected param of type Amount.');
        }

        return amount1.add(amount2);
    }

    static sum(amounts: Amount[]): Amount {
        return amounts.reduce((acc, val) => acc.add(val));
    }

    static isError(error: Error, message: string): boolean {
        return error.message === message;
    }

    valueOf(): number {
        return this.clone().changeAmountPrecisionTo(HIGHEST_PRECISION).attributes.value;
    }

    toJSON(): AmountType {
        return this.clone().attributes;
    }

    toFixed(): string {
        const { precision, value } = this.attributes;
        const precisionDivider = DECIMAL_PRECISION_MULTIPLIER ** precision;
        const result = value / precisionDivider;

        return result.toFixed(precision);
    }

    toString(): string {
        return `${this.toFixed()} ${this.attributes.currency}`;
    }

    equals(amount: Amount): boolean {
        return (
            this.attributes.value === amount.attributes.value &&
            this.attributes.precision === amount.attributes.precision &&
            this.attributes.currency === amount.attributes.currency
        );
    }

    clone(): Amount {
        return new Amount({
            value: this.attributes.value,
            precision: this.attributes.precision,
            currency: this.attributes.currency,
        });
    }

    add(amount: Amount): Amount {
        if (!(amount instanceof Amount)) {
            throw new Error('Expected param of type Amount.');
        }

        if (amount.attributes.currency !== this.attributes.currency) {
            throw new Error(DIFFERENT_AMOUNT_CURRENCIES_ERROR);
        }

        let firstAmount = this.clone();
        let secondAmount = amount.clone();
        const higherPrecision = Math.max(
            firstAmount.attributes.precision,
            secondAmount.attributes.precision,
        );

        if (firstAmount.attributes.precision !== higherPrecision) {
            firstAmount = firstAmount.changeAmountPrecisionTo(higherPrecision);
        }
        if (secondAmount.attributes.precision !== higherPrecision) {
            secondAmount = secondAmount.changeAmountPrecisionTo(higherPrecision);
        }

        return new Amount({
            value: firstAmount.attributes.value + secondAmount.attributes.value,
            precision: higherPrecision,
            currency: this.attributes.currency,
        });
    }

    subtract(amount: Amount): Amount {
        if (!(amount instanceof Amount)) {
            throw new Error('Expected param of type Amount.');
        }

        const amountToAdd = amount.clone();
        amountToAdd.attributes.value *= -1;
        return this.add(amountToAdd);
    }

    changeAmountPrecisionTo(newPrecision: number): Amount {
        if (typeof newPrecision !== 'number') {
            throw new Error('Precision is not a number!');
        }

        const originalPrecision = this.attributes.precision;
        let { value } = this.attributes;

        if (originalPrecision !== newPrecision) {
            value = Math.round(value * BASE_POW_VALUE ** (newPrecision - originalPrecision));
        }

        return new Amount({
            value,
            precision: newPrecision,
            currency: this.attributes.currency,
        });
    }

    compare(amount: Amount): -1 | 0 | 1 {
        const { value } = this.subtract(amount).attributes;

        if (value < 0) {
            return -1;
        }

        if (value > 0) {
            return 1;
        }

        return 0;
    }
}
